import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {INTRODUCTION_KEYWORD_ID, MAIN_CONTENT_POINTS} from '@axyseo/const/analysis';
import {enhanceResearcherForChinese} from '../../../languageProcessing/helpers/language/chineseHelperFactory.js';

/**
 * Assessment to check whether the keyphrase or synonyms are encountered in the first paragraph of the article.
 */
class IntroductionKeywordAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} [config] The configuration to use.
   * @param {string} [config.url] The URL to the relevant article on Yoast.com.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: INTRODUCTION_KEYWORD_ID,
      ctaType: 'fix',
      priority: 'low',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-audit/on-page-seo/checklist#keyword-in-introduction',
      fixPosition: 'highlightFirstParagraph',
      title: 'Introduction Keyword',
      content: {
        improve: '',
        bad: 'No keywords found in the first sentence. Add one to improve keyword rank.',
        good: 'Keyword is included in the first sentence.'
      }
    };

    this.identifier = INTRODUCTION_KEYWORD_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Assesses the presence of keyphrase or synonyms in the first paragraph.
   *
   * @param {Paper} paper The paper to use for the assessment.
   * @param {Researcher} researcher The researcher used for calling research.
   *
   * @returns {AssessmentResult} The result of this assessment.
   */
  getResult({paper, researcher}) {
    const assessmentResult = new AssessmentResult({config: this._config});

    // Enhance researcher for Chinese if needed
    const enhancedResearcher = enhanceResearcherForChinese(
      researcher,
      paper.getText(),
      paper.getKeyword()
    );

    this._firstParagraphMatches = enhancedResearcher.getResearch('findKeywordInFirstParagraph');
    const calculatedResult = this.calculateResult();

    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   * Checks if the paper has both keyword and text.
   *
   * @param {Paper} paper The paper to be analyzed.
   *
   * @returns {boolean} Whether the assessment is applicable or not.
   */
  isApplicable(paper) {
    return true;
  }

  /**
   *
   * @returns {{score: number, status: string}}
   */
  calculateResult() {
    let status = 'bad';

    if (
      this._firstParagraphMatches.foundInOneSentence ||
      this._firstParagraphMatches.foundInParagraph
    ) {
      status = 'good';
    }

    const score = this.getScore(MAIN_CONTENT_POINTS, status);

    return {
      score,
      status
    };
  }
}

export default IntroductionKeywordAssessment;
